import { useState, useEffect } from 'react';
import ModalBase from '@/components/base/ModalBase';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { PaymentProcessor } from '@/mocks/superAdminPaymentProcessors';

interface Props {
  open: boolean;
  onClose: () => void;
  processor: PaymentProcessor | null;
  onSave: (processor: PaymentProcessor, config: Record<string, string>, instructions: string, mode: boolean) => void;
}

export default function ProcessorConfigModal({ open, onClose, processor, onSave }: Props) {
  const [localConfig, setLocalConfig] = useState<Record<string, string>>({});
  const [localInstructions, setLocalInstructions] = useState('');
  const [localMode, setLocalMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !processor) return;
    const cfg: Record<string, string> = {};
    processor.config.forEach((c) => { cfg[c.key] = c.value; });
    setLocalConfig(cfg);
    setLocalInstructions(processor.manualInstructions || '');
    setLocalMode(processor.testMode);
    setErrors({});
  }, [open, processor]);

  if (!processor) return null;

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    processor.config.forEach((c) => {
      const val = (localConfig[c.key] || '').trim();
      if (val && c.key.includes('url')) {
        try { new URL(val); } catch { errs[c.key] = 'Ingresá una URL válida'; }
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      SwalAlert.warning('Campos inválidos', 'Revisá los campos marcados antes de guardar.');
      return;
    }
    setSaving(true);
    await new Promise((r) => setTimeout(r, 600));
    setSaving(false);
    onSave(processor, localConfig, localInstructions, localMode);
    SwalAlert.toast('Configuración guardada', 'success');
    onClose();
  };

  const isApi = processor.category === 'api';

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={`Configurar ${processor.name}`}
      subtitle={isApi ? 'Credenciales de conexión API' : 'Método de pago manual'}
      size="lg"
      headerIcon={
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${processor.logoColor}`}>
          <i className={`${processor.icon} text-sm`}></i>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Mode toggle for API processors */}
        {isApi && (
          <div className="bg-background-100 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-foreground-800 text-xs font-semibold">Modo de operación</p>
                <p className="text-foreground-400 text-[10px] mt-0.5">
                  {localMode ? 'Las transacciones no serán reales' : 'Se procesarán cobros reales'}
                </p>
              </div>
              <button
                onClick={() => setLocalMode(!localMode)}
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${localMode ? 'bg-amber-500' : 'bg-emerald-500'}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow-sm ${localMode ? 'translate-x-5.5' : 'translate-x-0.5'}`}></span>
              </button>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${localMode ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                <i className={`${localMode ? 'ri-flask-line' : 'ri-rocket-line'} text-[9px] mr-1`}></i>
                {localMode ? 'Sandbox' : 'Producción'}
              </span>
            </div>
          </div>
        )}

        {/* Config fields */}
        <div className="space-y-3">
          {processor.config.map((cfg) => (
            <div key={cfg.key}>
              <label className="block text-foreground-700 text-[10px] font-semibold mb-1">
                {cfg.label}
              </label>
              {cfg.type === 'textarea' ? (
                <textarea
                  value={localConfig[cfg.key] || ''}
                  onChange={(e) => setLocalConfig({ ...localConfig, [cfg.key]: e.target.value })}
                  rows={3}
                  maxLength={500}
                  className={`w-full px-3 py-2 bg-background-100 border rounded-lg text-xs text-foreground-800 font-mono outline-none resize-none transition-colors ${errors[cfg.key] ? 'border-red-300 bg-red-50/30' : 'border-background-200 focus:border-foreground-300'}`}
                  placeholder={cfg.placeholder}
                />
              ) : (
                <input
                  type={cfg.type === 'password' ? 'password' : 'text'}
                  value={localConfig[cfg.key] || ''}
                  onChange={(e) => setLocalConfig({ ...localConfig, [cfg.key]: e.target.value })}
                  className={`w-full px-3 py-2 bg-background-100 border rounded-lg text-xs text-foreground-800 font-mono outline-none transition-colors ${errors[cfg.key] ? 'border-red-300 bg-red-50/30' : 'border-background-200 focus:border-foreground-300'}`}
                  placeholder={cfg.placeholder}
                />
              )}
              {cfg.help && !errors[cfg.key] && (
                <p className="text-foreground-400 text-[9px] mt-1">{cfg.help}</p>
              )}
              {errors[cfg.key] && (
                <p className="text-red-500 text-[9px] mt-1 flex items-center gap-1">
                  <i className="ri-error-warning-line text-[10px]"></i>{errors[cfg.key]}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Manual instructions */}
        {!isApi && (
          <div>
            <label className="block text-foreground-700 text-[10px] font-semibold mb-1">
              Instrucciones de pago
            </label>
            <textarea
              value={localInstructions}
              onChange={(e) => setLocalInstructions(e.target.value)}
              rows={5}
              maxLength={500}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors resize-none"
              placeholder="Instrucciones visibles para el pagador..."
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-foreground-400 text-[9px]">Texto libre — visible en el paso de pago</p>
              <span className="text-foreground-400 text-[9px]">{localInstructions.length}/500</span>
            </div>
          </div>
        )}

        {/* Descriptor for API */}
        {isApi && (
          <div>
            <label className="block text-foreground-700 text-[10px] font-semibold mb-1">
              Descriptor del cargo
            </label>
            <input
              type="text"
              value={localConfig['descriptor'] || processor.descriptor}
              onChange={(e) => setLocalConfig({ ...localConfig, descriptor: e.target.value })}
              className="w-full px-3 py-2 bg-background-100 border border-background-200 rounded-lg text-xs text-foreground-800 font-mono outline-none focus:border-foreground-300 transition-colors"
              placeholder="Ej: RFW*"
            />
            <p className="text-foreground-400 text-[9px] mt-1">Texto que ve el cliente en su resumen bancario</p>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <ModalBase
        open={false}
        onClose={() => {}}
        title=""
        footer={
          <>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-background-200 text-foreground-600 text-xs font-bold hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {saving ? (
                <span className="flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin text-xs"></i>Guardando...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <i className="ri-save-line text-xs"></i>Guardar
                </span>
              )}
            </button>
          </>
        }
      />
    </ModalBase>
  );
}